import { useState } from "react";

/**
 * Customer mark. Companies with a real logo show it on a white tile; otherwise a logo-like monogram
 * in a stable brand hue (square for companies, round for private customers). Nothing is fabricated:
 * logos come from the customer record, monograms are initials only.
 *
 * `slot` reserves a fixed width so that names line up in tables/lists even though logos vary in width.
 */
const HUES = ["var(--brand-navy)", "var(--brand-teal)", "var(--brand-chocolate)", "var(--brand-blue)", "212 12% 42%", "var(--brand-brown)"];
const STOP = new Set(["as", "asa", "ans", "da", "sa", "og", "&", "–", "-", "familien", "entreprenør", "entreprenor", "bygg", "byggmester", "gruppen"]);

const hash = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
};

export const initialsOf = (name = "", type = "privat") => {
  const words = name.replace(/[.,()]/g, " ").split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (type === "privat") {
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  }
  const sig = words.filter((w) => !STOP.has(w.toLowerCase()));
  const pick = sig.length ? sig : words;
  if (pick.length === 1) {
    if (words.length > 1 && words[0] !== pick[0]) return (words[0][0] + pick[0][0]).toUpperCase();
    return pick[0].slice(0, 2).toUpperCase();
  }
  return (pick[0][0] + pick[1][0]).toUpperCase();
};

/* box = monogram square; logo = tile height/padding; slot = reserved width when aligned in lists. */
const SIZES = {
  xs: { box: "h-6 w-6 text-[10px] rounded-[5px]", logo: "h-6 rounded-[5px] px-1 py-[2px]", maxW: 72, slot: 56 },
  sm: { box: "h-7 w-7 text-[11px] rounded-[6px]", logo: "h-8 rounded-[7px] px-1.5 py-[3px]", maxW: 84, slot: 68 },
  md: { box: "h-9 w-9 text-[13px] rounded-[8px]", logo: "h-10 rounded-[9px] px-2 py-1", maxW: 112, slot: 92 },
  lg: { box: "h-12 w-12 text-[16px] rounded-[10px]", logo: "h-12 rounded-[10px] px-2.5 py-1.5", maxW: 150, slot: 124 },
  xl: { box: "h-16 w-16 text-[22px] rounded-[14px]", logo: "h-16 rounded-[14px] px-3 py-2", maxW: 220, slot: 180 },
};

const LogoTile = ({ name, logo, cls, maxW, className, testId, onBroken, title }) => (
  <span
    className={`company-logo-tile inline-flex shrink-0 select-none items-center justify-center overflow-hidden border bg-white ${cls} ${className}`}
    style={{ borderColor: "hsl(var(--border))", maxWidth: maxW }}
    title={title || name}
    data-testid={testId}
    data-logo="true"
  >
    <img src={logo} alt={`${name} logo`} className="h-full w-auto max-w-full object-contain" draggable="false" onError={onBroken} />
  </span>
);

export const CompanyMark = ({ name, type = "privat", logo, size = "sm", slot = false, className = "", testId }) => {
  const [broken, setBroken] = useState(false);
  const company = type !== "privat";
  const sz = SIZES[size] || SIZES.sm;
  const hue = HUES[hash(name) % HUES.length];

  let mark;
  if (logo && !broken) {
    mark = <LogoTile name={name} logo={logo} cls={sz.logo} maxW={slot ? sz.slot : sz.maxW} className={slot ? "" : className} testId={slot ? undefined : testId} onBroken={() => setBroken(true)} />;
  } else {
    const style = company
      ? { background: `hsl(${hue})`, color: "hsl(0 0% 100%)" }
      : { background: `hsl(${hue} / 0.12)`, color: `hsl(${hue})` };
    mark = (
      <span
        className={`inline-flex shrink-0 select-none items-center justify-center font-semibold tracking-tight ${sz.box} ${company ? "" : "!rounded-full"} ${slot ? "" : className}`}
        style={style}
        aria-hidden="true"
        title={name}
        data-testid={slot ? undefined : testId}
      >
        {initialsOf(name, type)}
      </span>
    );
  }

  if (!slot) return mark;
  return (
    <span className={`inline-flex shrink-0 items-center justify-center ${className}`} style={{ width: sz.slot }} data-testid={testId}>
      {mark}
    </span>
  );
};

/**
 * Wide logo for headers (customer detail): real logo at natural aspect on a quiet tile,
 * or the large monogram.
 */
export const CompanyLogo = ({ name, type, logo, website, className = "", testId }) => {
  const [broken, setBroken] = useState(false);
  if (logo && !broken) {
    return <LogoTile name={name} logo={logo} cls="h-[72px] px-5 py-3 rounded-[16px]" maxW={260} className={className} testId={testId} title={website || name} onBroken={() => setBroken(true)} />;
  }
  return <CompanyMark name={name} type={type} size="xl" className={className} testId={testId} />;
};

/** Mark + name inline, e.g. in a table cell. */
export const CustomerInline = ({ name, type, logo, size = "xs", className = "", sub }) => (
  <span className={`inline-flex min-w-0 items-center gap-2 ${className}`}>
    <CompanyMark name={name} type={type} logo={logo} size={size} />
    <span className="min-w-0">
      <span className="block truncate">{name}</span>
      {sub && <span className="t-meta block truncate">{sub}</span>}
    </span>
  </span>
);

/** Label for the kind of customer: industry when known, else the generic type. */
const TYPE_LABEL = { privat: "Privatkunde", entreprenor: "Entreprenør", borettslag: "Borettslag" };
export const customerKind = (c = {}) => c.industry || c.customer_industry || TYPE_LABEL[c.type || c.customer_type] || "";
